import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import {
  severityStyle, typeLabel, whyItMatters, getBookingId, getVehicleId,
  actionPlanFor, REASSIGN_ELIGIBLE_TYPES,
} from "../../lib/warnings";
import {
  AlertTriangle, Eye, MessageSquare, Send, ChevronDown, ChevronUp,
  ArrowRightLeft, MoreHorizontal, FileText, Car as CarIcon, AlertOctagon, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nProvider";
import ReassignVehicleModal from "../ReassignVehicleModal";
import InlineForm from "./InlineForm";
import WarningCloseForm from "./WarningCloseForm";

function fmt(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function StatusPill({ status, t }) {
  const map = {
    active:   { c: "bg-red-50 text-red-700 border-red-200",       l: t("warnings.status.active") },
    reviewed: { c: "bg-blue-50 text-blue-700 border-blue-200",    l: t("warnings.status.reviewed") },
    closed:   { c: "bg-slate-50 text-slate-700 border-slate-200", l: t("warnings.status.closed") },
    fixed:    { c: "bg-emerald-50 text-emerald-700 border-emerald-200", l: t("warnings.status.fixed") },
  };
  const s = map[status] || { c: "bg-slate-100 text-slate-700 border-slate-200", l: status };
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${s.c}`}>{s.l}</span>;
}

const ACTION_META = {
  reassign: { icon: ArrowRightLeft, labelKey: "warnings.action.reassign" },
  notify:   { icon: Send,           labelKey: "warnings.action.notify" },
  booking:  { icon: FileText,       labelKey: "warnings.action.open_booking" },
  incident: { icon: AlertOctagon,   labelKey: "warnings.action.open_incident" },
  vehicle_documents: { icon: CarIcon, labelKey: "warnings.action.open_vehicle_documents" },
  vehicle_maintenance: { icon: CarIcon, labelKey: "warnings.action.open_vehicle_maintenance" },
};

// onAction (path, body) => result, optional: sandboxed-demo hook, same
// convention as CustomerConfirmation.jsx/ReassignVehicleModal.jsx. When
// supplied, replaces every real `api.post(/warnings/{id}/...)` call below
// (review/close/note/notify-customer) so this card can be embedded in a
// public demo without ever touching the real API. reassignPreloaded/
// onReassignSubmit are forwarded straight through to the embedded
// ReassignVehicleModal for the same reason.
export default function WarningCard({ w, onChanged, onAction = null, reassignPreloaded = null, onReassignSubmit = null, onUiState = null }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [note, setNote] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const moreRef = useRef(null);

  const style = severityStyle(w.severity);
  const isOpen = ["active", "reviewed"].includes(w.status);
  const bookingId = getBookingId(w);
  const vehicleId = getVehicleId(w);
  const plan = actionPlanFor(w.warning_type);
  const canReassign = (kind) => kind === "reassign" && !!bookingId
    && REASSIGN_ELIGIBLE_TYPES.includes(w.warning_type);

  const linkClass = (variant) => {
    if (variant === "primary") return "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90";
    if (variant === "menu") return "w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2";
    return "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50";
  };

  useEffect(() => {
    if (!showMore) return;
    const onDocClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showMore]);

  const act = async (path, body = {}, successText) => {
    setBusy(true);
    try {
      if (onAction) await onAction(path, body);
      else await api.post(`/warnings/${w.id}/${path}`, body);
      toast.success(successText);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const renderActionButton = (kind, variant) => {
    if (!kind) return null;
    if (kind === "reassign") {
      if (!canReassign("reassign")) return null;
      return (
        <button
          key="reassign"
          data-testid={`warning-reassign-${w.id}`}
          onClick={() => setReassignOpen(true)}
          disabled={busy}
          className={variant === "primary"
            ? "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-60"
            : "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1B4332] text-[#1B4332] bg-white hover:bg-emerald-50 text-sm"}
        >
          <ArrowRightLeft className="w-3.5 h-3.5" /> {t(ACTION_META.reassign.labelKey)}
        </button>
      );
    }
    if (kind === "notify") {
      if (!bookingId) return null;
      return (
        <button
          key="notify"
          data-testid={`warning-notify-${w.id}`}
          onClick={() => {
            const next = !showNotify;
            setShowNotify(next);
            onUiState?.(next ? "notify-form" : "idle");
          }}
          disabled={busy}
          className={variant === "primary"
            ? "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-60"
            : "inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50"}
        >
          <Send className="w-3.5 h-3.5" /> {t(ACTION_META.notify.labelKey)}
        </button>
      );
    }
    if (kind === "booking") {
      if (!bookingId) return null;
      return (
        <Link
          key="booking"
          to={`/app/bookings/${bookingId}`}
          data-testid={`warning-open-booking-${w.id}`}
          className={linkClass(variant)}
        >
          <FileText className="w-3.5 h-3.5" /> {t(ACTION_META.booking.labelKey)}
        </Link>
      );
    }
    if (kind === "incident") {
      if (!w.related_incident_id) return null;
      return (
        <Link
          key="incident"
          to={`/app/incidents/${w.related_incident_id}`}
          data-testid={`warning-open-incident-${w.id}`}
          className={linkClass(variant)}
        >
          <AlertOctagon className="w-3.5 h-3.5" /> {t(ACTION_META.incident.labelKey)}
        </Link>
      );
    }
    if (kind === "vehicle_documents") {
      if (!vehicleId) return null;
      return (
        <Link
          key="vehicle_documents"
          to={`/app/vehicles/${vehicleId}?tab=documents`}
          data-testid={`warning-open-vehicle-documents-${w.id}`}
          className={linkClass(variant)}
        >
          <CarIcon className="w-3.5 h-3.5" /> {t(ACTION_META.vehicle_documents.labelKey)}
        </Link>
      );
    }
    if (kind === "vehicle_maintenance") {
      if (!vehicleId) return null;
      return (
        <Link
          key="vehicle_maintenance"
          to={`/app/vehicles/${vehicleId}?tab=maintenance`}
          data-testid={`warning-open-vehicle-maintenance-${w.id}`}
          className={linkClass(variant)}
        >
          <CarIcon className="w-3.5 h-3.5" /> {t(ACTION_META.vehicle_maintenance.labelKey)}
        </Link>
      );
    }
    return null;
  };

  const primaryBtn = renderActionButton(plan.primary, "primary");
  const secondaryBtn = renderActionButton(plan.secondary, "secondary");
  const extraMoreLink = plan.extra && plan.extra !== plan.primary && plan.extra !== plan.secondary
    ? plan.extra : null;

  return (
    <div
      className={`rounded-xl border ${style.banner} overflow-hidden`}
      data-testid={`warning-card-${w.id}`}
    >
      <div className={`h-1 ${style.bar}`} />
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={`w-9 h-9 rounded-lg grid place-items-center bg-white border ${style.chip} flex-shrink-0`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${style.title}`}>
                  {style.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${style.chip}`}>
                  {typeLabel(w.warning_type, t)}
                </span>
                <StatusPill status={w.status} t={t} />
              </div>
              <div className={`font-semibold mt-1 ${style.title}`}>{w.title}</div>
              <div className={`text-sm mt-0.5 ${style.body}`}>{w.message}</div>
              {whyItMatters(w.warning_type, t) && (
                <div className="text-xs mt-1.5 text-slate-500 italic">
                  {whyItMatters(w.warning_type, t)}
                </div>
              )}
              {w.recommended_action && (
                <div className="text-xs mt-2 text-slate-600">
                  <span className="font-semibold">{t("warnings.recommended")}:</span> {w.recommended_action}
                </div>
              )}

              {/* Affected: always visible, not hidden behind Details */}
              {(bookingId || vehicleId || w.related_incident_id) && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {bookingId && (
                    <Link to={`/app/bookings/${bookingId}`}
                      data-testid={`warning-link-booking-${w.id}`}
                      className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                      {t("warnings.affected.booking")}
                    </Link>
                  )}
                  {vehicleId && (
                    <Link to={`/app/vehicles/${vehicleId}`}
                      data-testid={`warning-link-vehicle-${w.id}`}
                      className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                      {t("warnings.affected.vehicle")}
                    </Link>
                  )}
                  {w.related_incident_id && (
                    <Link to={`/app/incidents/${w.related_incident_id}`}
                      data-testid={`warning-link-incident-${w.id}`}
                      className="text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                      {t("warnings.affected.incident")}
                    </Link>
                  )}
                </div>
              )}

              <div className="text-xs text-slate-500 mt-2">
                {t("warnings.detected_at")} {fmt(w.detected_at)} · {t("warnings.last_seen")} {fmt(w.last_detected_at)}
                {w.status === "closed" && w.closed_at && (
                  <> · {t("warnings.closed_at")} {fmt(w.closed_at)}
                    {w.closure_reason && ` (${t(`warnings.close.reason.${w.closure_reason}`)})`}
                  </>
                )}
              </div>
            </div>
          </div>
          {w.internal_notes?.length > 0 && (
            <button
              data-testid={`warning-expand-${w.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {expanded ? t("warnings.hide_notes") : t("warnings.show_notes")}
            </button>
          )}
        </div>

        {expanded && w.internal_notes?.length > 0 && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("warnings.internal_notes")}</div>
            <ul className="space-y-1 text-sm">
              {w.internal_notes.map((n, i) => (
                <li key={i} className="border-l-2 border-slate-300 pl-2">
                  <div>{n.note}</div>
                  <div className="text-[11px] text-slate-500">{fmt(n.at)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        {isOpen && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {primaryBtn}
            {secondaryBtn}
            <div className="relative" ref={moreRef}>
              <button
                data-testid={`warning-more-${w.id}`}
                onClick={() => setShowMore((v) => !v)}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm hover:bg-slate-50"
              >
                <MoreHorizontal className="w-3.5 h-3.5" /> {t("warnings.more_actions")}
              </button>
              {showMore && (
                <div className="absolute z-10 mt-1 w-56 rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-sm">
                  {extraMoreLink && (
                    <div onClick={() => setShowMore(false)}>
                      {renderActionButton(extraMoreLink, "menu")}
                    </div>
                  )}
                  {w.status === "active" && (
                    <button
                      data-testid={`warning-review-${w.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2"
                      onClick={() => { setShowMore(false); act("review", {}, t("warnings.toast.reviewed")); }}
                    >
                      <Eye className="w-3.5 h-3.5" /> {t("warnings.action.review")}
                    </button>
                  )}
                  <button
                    data-testid={`warning-add-note-${w.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2"
                    onClick={() => { setShowMore(false); setShowNote((v) => !v); }}
                  >
                    <MessageSquare className="w-3.5 h-3.5" /> {t("warnings.action.add_note")}
                  </button>
                  <button
                    data-testid={`warning-close-${w.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2 text-red-700"
                    onClick={() => { setShowMore(false); setShowClose((v) => !v); }}
                  >
                    <XCircle className="w-3.5 h-3.5" /> {t("warnings.action.close")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showClose && (
          <WarningCloseForm
            warningId={w.id}
            busy={busy}
            onClose={() => setShowClose(false)}
            onSubmit={async ({ reason, note: closeNote }) => {
              await act("close", { reason, note: closeNote }, t("warnings.toast.closed"));
              setShowClose(false);
            }}
          />
        )}

        {showNote && (
          <InlineForm
            placeholder={t("warnings.note_placeholder")}
            value={note}
            setValue={setNote}
            onClose={() => { setShowNote(false); setNote(""); }}
            onSubmit={async () => {
              if (!note.trim()) return toast.error(t("warnings.toast.note_empty"));
              await act("note", { note }, t("warnings.toast.note_added"));
              setShowNote(false); setNote("");
            }}
            submitLabel={t("warnings.save_note")}
            testIdPrefix={`warning-note-form-${w.id}`}
            busy={busy}
          />
        )}

        {showNotify && (
          <InlineForm
            placeholder={t("warnings.notify_placeholder")}
            value={notifyMsg}
            setValue={setNotifyMsg}
            onClose={() => { setShowNotify(false); setNotifyMsg(""); }}
            onSubmit={async () => {
              await act("notify-customer",
                notifyMsg.trim() ? { custom_message: notifyMsg } : {},
                t("warnings.toast.customer_notified"));
              setShowNotify(false); setNotifyMsg("");
            }}
            submitLabel={t("warnings.send_notification")}
            testIdPrefix={`warning-notify-form-${w.id}`}
            busy={busy}
            note={t("warnings.notify.disclaimer")}
          />
        )}
      </div>
      {canReassign("reassign") && (
        <ReassignVehicleModal
          bookingId={bookingId}
          open={reassignOpen}
          onClose={() => setReassignOpen(false)}
          onDone={onChanged}
          preloaded={reassignPreloaded}
          onSubmit={onReassignSubmit}
        />
      )}
    </div>
  );
}
